import React, {
    Component
} from 'react';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import {connect} from 'react-redux';
import memoize from 'memoize-one';
import parse from 'html-react-parser';
import {keys} from 'ts-transformer-keys';

import {Button} from 'primereact/button';
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';

import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import PropertySet from '~/shared/src/mm/PropertySet';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import User, {
    GenericPermissionName,
    View
} from '~/shared/src/model/User';
import {
    DataStructure,
    getReferenceSetsForDataObject
} from '~/shared/src/utilities/dataStructure';
import {BaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {
    ColumnConfig,
    ColumnDefinition,
    ColumnSorter
} from '~/shared/src/validationSchemes/modules/overview';
import {
    LoadFinderExportDataResult,
    LoadFinderOverviewDataParameters
} from '~/shared/src/utilities/modules/finder';
import {getXmlForDataXml} from '~/shared/src/utilities/xmlExport';

import {StoreState} from '~/frontend/src/store';
import {
    convertSortersToSortersWithField,
    convertSortersWithFieldToSorters
} from '~/frontend/src/components/base/DataTable/utilities';
import {
    getAllColumns,
    applyAllColumnsToColumns,
    getViews,
    getModuleForViewSelector,
    viewSelectorPanelPropsCompare,
    buildPropsFromViewProps
} from '~/frontend/src/components/base/ItemFinder/overview/utilities';
import {ComponentProps as OverviewComponentProps} from '~/frontend/src/modules/panels/Finder/overview';
import {ViewOption} from '~/frontend/src/components/base/shared/field/ViewSelectorDropdown';
import {
    ColumnConfigMenuColumn,
    ColumnConfigMenuColumnsGroup
} from '~/frontend/src/components/base/shared/menu/ColumnConfigMenu';
import {
    ColumnSortMenuColumnsGroup,
    ColumnSortMenuColumnSorter
} from '~/frontend/src/components/base/shared/menu/ColumnSortMenu';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';
import {fileDownload} from '~/frontend/src/utilities/fileDownload';
import {getModifiedData} from '~/frontend/src/utilities/dataUtils';

import xhr from '~/frontend/src/utilities/xhr';
import * as PanelManagerActions from '~/frontend/src/actions/panelManager';
import {OpenPanelForItemType} from '~/frontend/src/actions/panelManager/openPanelForItemType';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import saveData, {SaveData} from '~/frontend/src/actions/panel/dataTable/saveData';
import * as FinderOverviewActions from '~/frontend/src/actions/panel/finderOverview';
import {ClearSearch} from '~/frontend/src/actions/panel/finderOverview/clearSearch';
import {SetItemTypeName} from '~/frontend/src/actions/panel/finderOverview/setItemTypeName';
import {SetSchema} from '~/frontend/src/actions/panel/finderOverview/setSchema';
import {SetAdvancedSearchOptions} from '~/frontend/src/actions/panel/finderOverview/setAdvancedSearchOptions';
import * as ElusiveDataActions from '~/frontend/src/actions/elusiveData';
import {
    SetElusiveData,
    UnsetElusiveData,
    ClearElusiveData
} from '~/frontend/src/actions/elusiveData';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import {setMessage} from '~/frontend/src/actions/intercom';
import {XlsxExport} from '~/frontend/src/components/base/DataTable/XlsxExport';


const Actions = Object.assign( { setMessage }, PanelManagerActions, PanelActions, { saveData }, FinderOverviewActions, ElusiveDataActions ) as any;
type SetParam = <K extends keyof OverviewComponentProps>( panelKey : string, paramKey : K, paramValue : OverviewComponentProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof OverviewComponentProps ) => UnsetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<OverviewComponentProps> ) => SetParamsAction;


export const defaultProps = {

    /**
     * The structure of the column data.
     */
    dataStructure: {} as DataStructure,

    /**
     * The column definition of the columns possible to show in the DataTable.
     */
    columnDefinitions: [] as ColumnDefinition[],

    /**
     * The column configuration of the columns to show in the DataTable.
     */
    columns: [] as ColumnConfig[],

    /**
     * The column sorters.
     */
    sorters: [] as ColumnSorter[],

    /**
     * Search result loaded from the backend.
     */
    search: false as boolean,

    /**
     * The current panelProps.
     *
     * Only applicable if <prop>showViewSelector</prop> is set to true.
     */
    viewSelectorPanelProps: {} as PanelProps,

    /**
     * Should the save button shown in the toolbar.
     */
    showSaveButton: true as boolean,

    /**
     * The error count to show as badge on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true.
     */
    saveButtonErrorCount: 0 as number,

    /**
     * The error tooltip to show on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true AND <prop>saveButtonErrorCount</prop> is set to an positive integer.
     */
    saveButtonErrorTooltip: '' as string | JSX.Element | ( string | JSX.Element )[],

    /**
     * Should the add button shown in the toolbar.
     */
    showAddButton: false as boolean,

    /**
     * Show copy XML button.
     */
    showCopyXmlButton: true as boolean,

    /**
     * Show download XML button.
     */
    showDownloadXmlButton : true as boolean,
    showDownloadXlsxButton: true as boolean,

    /**
     * Should the SchemaDropdown shown in the toolbar.
     */
    showSchemaDropdown: true as boolean,

    /**
     * Should the ItemTypeDropdown shown in the toolbar.
     */
    showItemTypeDropdown: true as boolean,

    /**
     * Should the SearchField shown in the toolbar.
     */
    showSearchField: true as boolean,

    /**
     * Should the AdvancedSearchOptionsMenu shown in the toolbar.
     */
    showAdvancedSearchOptions: true as boolean,

    /**
     * Show clear search button.
     */
    showClearSearchButton: true as boolean,

    /**
     * Should the ViewSelectorDropdown shown in the toolbar.
     */
    showViewSelector: true as boolean,

    /**
     * Should the view options button shown in the toolbar.
     */
    showViewOptionsButton: true as boolean,

    /**
     * Should the column options button shown in the toolbar.
     */
    showColumnOptionsButton: true as boolean,

    /**
     * Should the column sort button shown in the toolbar.
     */
    showColumnSortButton: true as boolean,

    /**
     * Show reload button.
     */
    showReloadButton: true as boolean,

    /**
     * Is the overview is editable.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true. Used to enable/disable him.
     */
    editable: false as boolean,

    tabIndexOffset: 500 as number
};

export type DefaultProps = typeof defaultProps;

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext to access global.data with.
     */
    dataContext : string;

    /**
     * The database schema to filter data and metadata for.
     */
    schema : Schema;

    /**
     * The status to filter data for.
     */
    status : Status;

    /**
     * The timestamp to filter data at.
     */
    timestamp : string;

    /**
     * The timezone to filter data at.
     */
    timezone : string;

    /**
     * The ItemTypeName to filter data and metadata for.
     */
    itemTypeName : string;

    /**
     * The search string to filter data for.
     */
    searchString : string;

    /**
     * The URL for the route to load the data for exports
     */
    exportLoadDataUrl? : string,

    /**
     * The parameters for the route to load the data for exports
     */
    exportLoadDataParameters? : BaseRequestParameters & LoadFinderOverviewDataParameters,

    /**
     * Should default values shown for properties.
     */
    showPropertyDefaults : boolean;

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode : boolean;

    /**
     * Should the MetaModel information as defined additionally shown for processed information.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames : boolean;

    /**
     * Is the overview in editMode.
     *
     * Only applicable if <prop>editable</prop> is set to true.
     */
    editMode : boolean;

    /**
     * Should a legend bar shown.
     */
    legend : boolean;
};

export type ComponentProps = Partial<DefaultProps> & Props;
export const componentProps = keys<ComponentProps>();

type StateProps = {

    /**
     * The elusive data (not persisted by redux store) for this component.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveDataState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

type ActionProps = {
    // panel manager actions
    openPanelForItemType : OpenPanelForItemType;

    // common panel actions
    setParam : SetParam;
    unsetParam : UnsetParam;
    setParams : SetParams;

    //dataTable actions
    saveData : SaveData;
    clearSearch : ClearSearch;

    // finderOverview actions
    setItemTypeName : SetItemTypeName;
    setSchema : SetSchema;
    setAdvancedSearchOptions : SetAdvancedSearchOptions;

    // intercom actions
    setMessage : typeof setMessage;

    // elusiveData actions
    setElusiveData : SetElusiveData;
    unsetElusiveData : UnsetElusiveData;
    clearElusiveData : ClearElusiveData;
}

export type AllProps = DefaultProps & Props & StateProps & ActionProps;


type State = {
    mobile : boolean;
};


const getMobile = () => matchMedia( 'screen and (max-width: 960px)' ).matches ? true : false;


/**
 * Toolbar
 */
export class Toolbar extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            mobile: getMobile()
        };

        this.getViewsForViewSelectorDropdown = memoize( this.getViewsForViewSelectorDropdown );
        this.getSelectedViewForViewSelectorDropdown = memoize( this.getSelectedViewForViewSelectorDropdown );
        this.getAllColumns = memoize( this.getAllColumns );
        this.getSortersWithField = memoize( this.getSortersWithField );
        this.isInsertable = memoize( this.isInsertable );

        this.onMatchMobileChangeHandler = this.onMatchMobileChangeHandler.bind( this );
        this.onSaveButtonClick = this.onSaveButtonClick.bind( this );
        this.onAddItem = this.onAddItem.bind( this );
        this.onCopyXmlApply = this.onCopyXmlApply.bind( this );
        this.onDownloadXmlApply = this.onDownloadXmlApply.bind( this );
        this.onDownloadXlsxClick = this.onDownloadXlsxClick.bind( this );
        this.onSchemaDropdownChange = this.onSchemaDropdownChange.bind( this );
        this.onItemTypeDropdownChange = this.onItemTypeDropdownChange.bind( this );
        this.onSearchFieldChange = this.onSearchFieldChange.bind( this );
        this.onSearchFieldClear = this.onSearchFieldClear.bind( this );
        this.onSearchFieldSearch = this.onSearchFieldSearch.bind( this );
        this.onAdvancedSearchOptionsMenuChange = this.onAdvancedSearchOptionsMenuChange.bind( this );
        this.onClearSearchButtonClick = this.onClearSearchButtonClick.bind( this );
        this.onViewOptionChange = this.onViewOptionChange.bind( this );
        this.onExpandColumnsToHeaderWidth = this.onExpandColumnsToHeaderWidth.bind( this );
        this.onExpandColumnsToScreenWidth = this.onExpandColumnsToScreenWidth.bind( this );
        this.onColumnVisibilityChange = this.onColumnVisibilityChange.bind( this );
        this.onColumnSortChange = this.onColumnSortChange.bind( this );
        this.onViewSelectorSetView = this.onViewSelectorSetView.bind( this );
        this.onReloadButtonClick = this.onReloadButtonClick.bind( this );
    }

    componentDidMount() {
        matchMedia( 'screen and (max-width: 960px)' ).addEventListener( 'change', this.onMatchMobileChangeHandler );
    }

    componentWillUnmount() {
        matchMedia( 'screen and (max-width: 960px)' ).removeEventListener( 'change', this.onMatchMobileChangeHandler );
    }

    onMatchMobileChangeHandler() {
        this.setState( { mobile: getMobile() } );
    }

    /**
     * Handler for Save button click.
     *
     * @private
     */
    onSaveButtonClick() {
        this.props.saveData( this.props.uid, this.props.schema );
    }

    onAddItem( event : React.MouseEvent<HTMLElement> ) {
        const {
                  schema,
                  timezone,
                  itemTypeName,
                  user
              } = this.props;

        this.props.openPanelForItemType(
            schema,
            itemTypeName,
            {
                schema,
                status   : 'active',
                timestamp: 'now',
                timezone,
                //
                createNewItem: {
                    itemTypeId: metaModel[ schema ].getItemType( itemTypeName )!.id,
                    statusId  : metaModel[ schema ].getStatus( 'active' )!.id
                }
            },
            undefined,
            event,
            !user?.hasGenericPermissionOnItemType( 'nonBlocked', itemTypeName, schema ) ? 'nongeneric' : undefined
        );
    }

    async onCopyXmlApply( selectedPropertySets : PropertySet[], selectedEnumPropertySets : PropertySet[], selectedReferenceSets : {
        dest : ReferenceSet[];
        src : ReferenceSet[];
    } ) {
        // show data requested toast
        this.props.setMessage(
            this.props.uid,
            'toast',
            'xmlDataRequested',
            {
                severity: 'info',
                summary : __( 'YN.base.common.xml.dataRequested.title' ),
                detail  : parse( __( 'YN.base.common.xml.dataRequested.text' ) ),
                closable: true,
                sticky  : true
            }
        );

        // request data
        const dataXml = await this.loadXmlExportData( selectedPropertySets, selectedEnumPropertySets, selectedReferenceSets );

        // show data received toast
        this.props.setMessage(
            this.props.uid,
            'toast',
            'xmlDataReceived',
            {
                severity: 'info',
                summary : __( 'YN.base.common.xml.dataReceived.title' ),
                detail  : <>
                    {parse( __( 'YN.base.common.xml.dataReceived.text' ) )}<br/>
                    <br/>
                    <Button
                        label={__( 'YN.base.common.copyToClipboard' )}
                        onClick={() => {
                            const fnc = async () => {
                                await navigator.clipboard.writeText( getXmlForDataXml( dataXml, this.props.exportLoadDataParameters?.schema ?? this.props.schema ) );

                                this.props.setMessage(
                                    this.props.uid,
                                    'toast',
                                    'xmlDataCopiedToClipboard',
                                    {
                                        severity: 'info',
                                        summary : __( 'YN.base.common.xml.dataCopiedToClipboard.title' ),
                                        detail  : parse( __( 'YN.base.common.xml.dataCopiedToClipboard.text' ) )
                                    }
                                );
                            };

                            void fnc();
                        }}
                        className="mr-3 p-button-outlined"
                    />
                </>,
                closable: true,
                sticky  : true
            }
        );
    }

    async onDownloadXmlApply( selectedPropertySets : PropertySet[], selectedEnumPropertySets : PropertySet[], selectedReferenceSets : {
        dest : ReferenceSet[];
        src : ReferenceSet[];
    } ) {
        // show download requested toast
        this.props.setMessage(
            this.props.uid,
            'toast',
            'xmlDownloadRequested',
            {
                severity: 'info',
                summary : __( 'YN.base.common.xml.downloadRequested.title' ),
                detail  : parse( __( 'YN.base.common.xml.downloadRequested.text' ) ),
                closable: true,
                sticky  : true
            }
        );

        // request data
        const dataXml  = await this.loadXmlExportData( selectedPropertySets, selectedEnumPropertySets, selectedReferenceSets ),
              fileName = `FinderOverview_${this.props.itemTypeName || 'various'}`;

        fileDownload( getXmlForDataXml( dataXml, this.props.exportLoadDataParameters?.schema ?? this.props.schema ), fileName, 'xml' );

        this.props.setMessage(
            this.props.uid,
            'toast',
            'xmlDownloadComplete',
            {
                severity: 'info',
                summary : __( 'YN.base.common.xml.downloadComplete.title' ),
                detail  : parse( __( 'YN.base.common.xml.downloadComplete.text' ) )
            }
        );
    }

    async onDownloadXlsxClick() { // FIXME: fix type of data
        // show download requested toast
        this.props.setMessage(
            this.props.uid,
            'toast',
            'xlsxDownloadRequested',
            {
                severity: 'info',
                summary : __( 'YN.base.common.xlsx.downloadRequested.title' ),
                detail  : parse( __( 'YN.base.common.xlsx.downloadRequested.text' ) ),
                closable: true,
                sticky  : true
            }
        );

        // request data
        const fileContent = await XlsxExport.getXlsxFileForItemFinderData( this.props ),
              fileName    = `FinderOverview_${this.props.itemTypeName || 'various'}`;

        if ( fileContent === undefined ) {
            // FIXME: Error-Message
            this.props.setMessage(
                this.props.uid,
                'toast',
                'xlsxDownloadFailed',
                {
                    severity: 'error',
                    summary : __( 'YN.base.common.xlsx.downloadFailed.title' ),
                    detail  : parse( __( 'YN.base.common.xlsx.downloadFailed.text' ) )
                }
            );
        }
        else {
            fileDownload( fileContent, fileName, 'xlsx' );
            this.props.setMessage(
                this.props.uid,
                'toast',
                'xlsxDownloadComplete',
                {
                    severity: 'success',
                    summary : __( 'YN.base.common.xlsx.downloadComplete.title' ),
                    detail  : parse( __( 'YN.base.common.xlsx.downloadComplete.text' ) )
                }
            );
        }

    }

    /**
     * Handler for SchemaDropdown change event
     *
     * @param {String} schema
     *
     * @private
     */
    onSchemaDropdownChange( schema : Schema ) {
        this.props.setSchema( this.props.uid, this.props.dataContext, schema );
    }

    /**
     * Handler for ItemTypeDropdown change event
     *
     * @param {String} itemTypeName
     *
     * @private
     */
    onItemTypeDropdownChange( itemTypeName : string ) {
        this.props.setItemTypeName( this.props.uid, this.props.dataContext, this.props.schema, itemTypeName );
    }

    /**
     * Handler for SearchField change event
     *
     * @param {String} searchString
     *
     * @private
     */
    onSearchFieldChange( searchString : string ) {
        this.props.setParam( this.props.uid, 'searchString', searchString );
    }

    /**
     * Handler for SearchField clear event
     *
     * @private
     */
    onSearchFieldClear() {
        this.props.setParam( this.props.uid, 'searchString', '' );
    }

    /**
     * Handler for SearchField search event.
     *
     * @private
     */
    onSearchFieldSearch() {
        this.props.setParam( this.props.uid, 'doSearch', true );
        // force reload
        this.props.setElusiveData( this.props.uid, { lastParams: undefined } );
    }

    /**
     * Handler for the AdvancedSearchOptionsMenu change event.
     *
     * @param {Status} status
     * @param {String} timezone
     * @param {String} timestamp
     *
     * @private
     */
    onAdvancedSearchOptionsMenuChange( status : Status, timezone : string, timestamp : string ) {
        this.props.setAdvancedSearchOptions( this.props.uid, this.props.dataContext, this.props.schema, status, timezone, timestamp );
    }

    /**
     * Handler for SearchField clear event
     *
     * @private
     */
    onClearSearchButtonClick() {
        this.props.clearSearch( this.props.uid );
    }

    /**
     * The handler for view option change.
     *
     * @param {string} name
     * @param {Boolean} checked
     *
     * @private
     */
    onViewOptionChange( name : string, checked : boolean ) {
        if ( name === 'editMode' ) {
            this.props.unsetParam( this.props.uid, 'selection' );
        }

        this.props.setParam( this.props.uid, name as keyof OverviewComponentProps, checked );
    }

    /**
     * The handler for expand columns to header width.
     *
     * @private
     */
    onExpandColumnsToHeaderWidth() {
        this.props.setParam( this.props.uid, 'autoLayout', true );
    }

    /**
     * The handler for expand columns to screen width.
     *
     * @private
     */
    onExpandColumnsToScreenWidth() {
        this.props.setParam( this.props.uid, 'forceFit', true );
    }

    /**
     * Handler for column visibility change.
     *
     * @param {ColumnConfigMenuColumn[]} allColumnsGrouped
     *
     * @private
     */
    onColumnVisibilityChange( allColumnsGrouped : ColumnConfigMenuColumn[] ) {
        const {
                  uid,
                  columnDefinitions,
                  columns,
                  sorters,
                  search
              }                                            = this.props,
              newColumns                                   = applyAllColumnsToColumns( allColumnsGrouped as ColumnConfigMenuColumnsGroup[], columnDefinitions, columns ),
              newSorters                                   = ( [] as ColumnSorter[] ).concat( sorters ),
              propsToSet : Partial<OverviewComponentProps> = {
                  doSearch: search,
                  columns : newColumns
              };

        // remove sorters for removed columns
        for ( const oldColumn of columns ) {

            // column removed?
            if ( !newColumns.find( column => column.field === oldColumn.field ) ) {
                const sorterIndex = newSorters.findIndex( sorter => sorter.field === oldColumn.field );

                // sorter found?
                if ( sorterIndex !== -1 ) {
                    newSorters.splice( sorterIndex, 1 );

                    if ( !propsToSet.sorters ) {
                        propsToSet.sorters = newSorters;
                    }
                }
            }
        }

        this.props.setParams( uid, propsToSet );
    }

    /**
     * Handler for column sort change.
     *
     * @param {ColumnSortMenuColumnSorter[]} sortersWithField
     *
     * @private
     */
    onColumnSortChange( sortersWithField : ColumnSortMenuColumnSorter[] ) {
        const {
                  uid,
                  search
              }                                            = this.props,
              propsToSet : Partial<OverviewComponentProps> = {
                  doSearch: search,
                  sorters : convertSortersWithFieldToSorters( sortersWithField, this.props.columnDefinitions )
              };

        this.props.setParams( uid, propsToSet );
    }

    /**
     * Handler for viewSelector set view.
     *
     * @param {PanelProps} panelProps
     *
     * @private
     */
    onViewSelectorSetView( panelProps : PanelProps ) {
        this.props.setParams( this.props.uid, buildPropsFromViewProps( panelProps, this.props.dataStructure, this.props.schema, this.props.status, this.props.timestamp, this.props.itemTypeName, this.props.user! ) );
    }

    /**
     * Handler for reload Button click.
     *
     * @private
     */
    onReloadButtonClick() {
        const {
                  uid
              } = this.props;

        this.props.setElusiveData( uid, { lastParams: undefined } );
        this.props.setParam( uid, 'doSearch', true );
    }

    /**
     * Gets the views from user.
     *
     * @param {Schema} schema
     * @param {string} itemTypeName
     * @param {User} user
     */
    getViewsForViewSelectorDropdown( schema : Schema, itemTypeName : string, user : User ) {
        return getViews( schema, itemTypeName, user );
    }

    /**
     * Gets the view from given views which match the given panelProps.
     *
     * @param {Schema} schema
     * @param {string} itemTypeName
     * @param {User} user
     * @param {Object} [panelProps=undefined]
     */
    getSelectedViewForViewSelectorDropdown( schema : Schema, itemTypeName : string, user : User, panelProps : object | undefined ) {
        const views = this.getViewsForViewSelectorDropdown( schema, itemTypeName, user );

        let retVal : View | undefined;

        if ( panelProps ) {
            for ( const view of views ) {
                if ( viewSelectorPanelPropsCompare( panelProps as PanelProps, view.panelProps ) ) {
                    retVal = view;
                    break;
                }
            }
        }

        return retVal;
    }

    /**
     * Memoizable wrapper for getAllColumns
     *
     * @private
     */
    getAllColumns( columnDefinitions : ColumnDefinition[], columns : ColumnConfig[], dataStructure : DataStructure, schema : Schema, status : Status, timestamp : string, itemTypeName : string, rawMode : boolean, showAdditionalRawNames : boolean, user : User ) : ColumnSortMenuColumnsGroup[] {
        return getAllColumns( columnDefinitions, columns, dataStructure, schema, status, timestamp, itemTypeName, rawMode, showAdditionalRawNames, user );
    }

    /**
     * Memoizable wrapper for getSortersWithFieldFromSorters
     *
     * @private
     */
    getSortersWithField( sorters : ColumnSorter[] ) : ColumnSortMenuColumnSorter[] {
        return convertSortersToSortersWithField( sorters );
    }

    isInsertable( schema : Schema, itemTypeName : string, user : User | undefined, genericPermission : GenericPermissionName = 'nonBlocked' ) : boolean {
        const permission = 'insert';

        let retVal : boolean = false;

        if ( user ) {
            if ( itemTypeName ) {
                // check if insertable
                if (
                    user.hasPermissionOnItemType( permission, itemTypeName, schema )
                    && (
                        user.hasGenericPermissionOnItemType( genericPermission, itemTypeName, schema )
                        || (
                            ( schema === 'CFG' && itemTypeName === 'Group' )
                            && ( !!global.user?.isMemberOfGroup( config.sysGroups?.groupAdmin ?? '' ) || !!global.user?.isMemberOfGroup( config.sysGroups?.admin ?? '' ) )
                        )
                        || (
                            ( schema === 'CFG' && itemTypeName === 'User' )
                            && ( !!global.user?.isMemberOfGroup( config.sysGroups?.userAdmin ?? '' ) || !!global.user?.isMemberOfGroup( config.sysGroups?.admin ?? '' ) )
                        )
                        || (
                            ( schema === 'CFG' && itemTypeName === 'YnItemType' )
                            && ( !!global.user?.isMemberOfGroup( config.sysGroups?.metaModelAdmin ?? '' ) || !!global.user?.isMemberOfGroup( config.sysGroups?.admin ?? '' ) )
                        )
                    )
                ) {
                    retVal = true;
                }
            }

            // check if any itemType is insertable
            else {
                const itemTypes = metaModel[ schema ].getItemTypes();

                for ( const itemType of itemTypes ) {
                    if (
                        itemType.id > 0
                        && user.hasPermissionOnItemType( permission, itemType.id, schema )
                        && user.hasGenericPermissionOnItemType( genericPermission, itemType.id, schema )
                    ) {
                        retVal = true;
                        break;
                    }
                }
            }
        }

        return retVal;
    }

    async loadXmlExportData( selectedPropertySets : PropertySet[], selectedEnumPropertySets : PropertySet[], selectedReferenceSets : {
        dest : ReferenceSet[];
        src : ReferenceSet[];
    } ) : Promise<string> {
        const {
                  exportLoadDataParameters
              }        = this.props,
              response = await xhr.post(
                  `${BASE_URL}/api/finder/loadFinderExportData`,
                  {
                      ...exportLoadDataParameters,
                      propertySets : selectedPropertySets.concat( selectedEnumPropertySets ).map( propertySet => propertySet.id ),
                      referenceSets: selectedReferenceSets.dest.concat( selectedReferenceSets.src ).map( referenceSet => referenceSet.id )
                  },
                  undefined,
                  undefined,
                  'loadXmlExportData'
              );

        let retVal : string = '';

        if ( response?.data ) {
            const responseData = response.data as LoadFinderExportDataResult;

            if ( responseData.success ) {
                retVal = responseData.data.dataXml;
            }
        }

        return retVal;
    }

    getExportParameters() {
        const retVal   = {
                  allPropertySets           : [] as PropertySet[],
                  selectablePropertySets    : [] as PropertySet[],
                  selectedPropertySets      : [] as PropertySet[],
                  allEnumPropertySets       : [] as PropertySet[],
                  selectableEnumPropertySets: [] as PropertySet[],
                  selectedEnumPropertySets  : [] as PropertySet[],
                  allReferenceSets          : {
                      dest: [],
                      src : []
                  } as {
                      dest : ReferenceSet[];
                      src : ReferenceSet[];
                  },
                  selectableReferenceSets   : {
                      dest: [],
                      src : []
                  } as {
                      dest : ReferenceSet[];
                      src : ReferenceSet[];
                  },
                  selectedReferenceSets     : {
                      dest: [],
                      src : []
                  } as {
                      dest : ReferenceSet[];
                      src : ReferenceSet[];
                  },
                  referenceItemType         : ''
              },
              {
                  schema,
                  itemTypeName,
                  dataStructure,
                  columns,
                  columnDefinitions
              }        = this.props,
              itemType = metaModel[ schema ].getItemType( itemTypeName );

        if ( itemType ) {
            for ( const propertySet of itemType.propertySets ) {
                if ( propertySet.isEnumerated ) {
                    retVal.allEnumPropertySets.push( propertySet );
                }
                else {
                    retVal.allPropertySets.push( propertySet );
                }
            }
            retVal.selectablePropertySets = retVal.allPropertySets;
            retVal.selectableEnumPropertySets = retVal.allEnumPropertySets;

            retVal.allReferenceSets.dest = metaModel[ schema ].getReferenceSetsForItemType( itemType.id, 'dest' ) ?? [];
            retVal.selectableReferenceSets.dest = retVal.allReferenceSets.dest;
            retVal.allReferenceSets.src = metaModel[ schema ].getReferenceSetsForItemType( itemType.id, 'src' ) ?? [];
            retVal.selectableReferenceSets.src = retVal.allReferenceSets.src;

            // set visible propertySets and referenceSets as selected
            for ( const column of columns ) {
                const columnDefinition = columnDefinitions.find( colDef => colDef.field === column.field );

                if ( columnDefinition ) {
                    if ( columnDefinition.dataDescriptor.type === 'property' ) {
                        const propertySet     = retVal.allPropertySets.find( pSet => pSet.name === columnDefinition.dataDescriptor.propertyTypeName ),
                              enumPropertySet = retVal.allEnumPropertySets.find( pSet => pSet.name === columnDefinition.dataDescriptor.propertyTypeName );

                        if ( propertySet ) {
                            retVal.selectedPropertySets.push( propertySet );
                        }
                        if ( enumPropertySet ) {
                            retVal.selectedEnumPropertySets.push( enumPropertySet );
                        }
                    }

                    else if ( columnDefinition.dataDescriptor.type === 'item' ) {
                        if ( columnDefinition.dataDescriptor.dataObject.origId !== dataStructure.origId ) {
                            const referenceSets = getReferenceSetsForDataObject( columnDefinition.dataDescriptor.dataObject, dataStructure, schema );

                            if ( referenceSets.length ) {
                                for ( const referenceSet of referenceSets ) {
                                    const destReferenceSet = retVal.allReferenceSets.dest.find( rSet => rSet.id === referenceSet.id ),
                                          srcReferenceSet  = retVal.allReferenceSets.src.find( rSet => rSet.id === referenceSet.id );

                                    if ( destReferenceSet ) {
                                        retVal.selectedReferenceSets.dest.push( destReferenceSet );
                                    }
                                    if ( srcReferenceSet ) {
                                        retVal.selectedReferenceSets.src.push( srcReferenceSet );
                                    }
                                }
                            }
                        }
                    }
                }
            }

            retVal.referenceItemType = itemType.name;
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  schema,
                  status,
                  timestamp,
                  timezone,
                  dataContext,
                  itemTypeName,
                  searchString,
                  dataStructure,
                  columnDefinitions,
                  columns,
                  sorters,
                  showPropertyDefaults,
                  rawMode,
                  showAdditionalRawNames,
                  legend,
                  editable,
                  editMode,
                  showSaveButton,
                  saveButtonErrorCount,
                  saveButtonErrorTooltip,
                  showAddButton,
                  showCopyXmlButton,
                  showDownloadXmlButton,
                  showDownloadXlsxButton,
                  showSchemaDropdown,
                  showItemTypeDropdown,
                  showSearchField,
                  showAdvancedSearchOptions,
                  showClearSearchButton,
                  showViewSelector,
                  viewSelectorPanelProps,
                  showViewOptionsButton,
                  showColumnOptionsButton,
                  showColumnSortButton,
                  showReloadButton,
                  tabIndexOffset,
                  user
              }                                          = this.props,
              {
                  mobile
              }                                          = this.state,
              layoutComponents : ToolbarLayoutComponents = {
                  left : [],
                  right: []
              };

        // left
        if ( editable && showSaveButton ) {
            layoutComponents.left.push( {
                type  : 'saveButton',
                saveButtonErrorCount,
                saveButtonErrorTooltip,
                params: {
                    key: `${uid}_saveButton`,

                    className: 'yn-responsive-hidden',
                    disabled : !editMode,
                    onClick  : this.onSaveButtonClick,
                    tabIndex : tabIndexOffset + 10
                }
            } );
        }

        if ( showAddButton ) {
            let addButtonDisabled : boolean = true;

            if ( itemTypeName ) {
                addButtonDisabled = !(
                    this.isInsertable( schema, itemTypeName, user )
                    || (
                        this.isInsertable( schema, itemTypeName, user, 'none' )
                        && !!user?.getPanelPropsForItemType( schema, itemTypeName )?.length
                    )
                );
            }

            layoutComponents.left.push( {
                type  : 'addButton',
                params: {
                    key: `${uid}_addButton`,

                    disabled: addButtonDisabled,
                    onClick : this.onAddItem,
                    tabIndex: tabIndexOffset + 20
                }
            } );
        }

        layoutComponents.left.push( {
            type  : 'separator',
            params: {
                className: 'yn-responsive-hidden'
            }
        } );

        if ( showCopyXmlButton ) {
            layoutComponents.left.push( {
                type  : 'copyXml',
                params: {
                    key: `${uid}_copyXmlButton`,

                    uid,

                    ...this.getExportParameters(),

                    className: 'yn-responsive-hidden',
                    onApply  : ( ...args ) => {
                        void this.onCopyXmlApply( ...args );
                    },
                    tabIndex : tabIndexOffset + 30
                }
            } );
        }

        if ( showDownloadXmlButton ) {
            layoutComponents.left.push( {
                type  : 'downloadXml',
                params: {
                    key: `${uid}_downloadXmlButton`,

                    uid,

                    ...this.getExportParameters(),

                    className: 'yn-responsive-hidden',
                    onApply  : ( ...args ) => {
                        void this.onDownloadXmlApply( ...args );
                    },
                    tabIndex : tabIndexOffset + 40
                }
            } );
        }

        if ( showDownloadXlsxButton ) {
            layoutComponents.left.push( {
                type  : 'downloadXlsx',
                params: {
                    key: `${uid}_downloadXlsxButton`,

                    className: 'yn-responsive-hidden',
                    onClick  : () => {
                        void this.onDownloadXlsxClick();
                    },
                    tabIndex : tabIndexOffset + 50
                }
            } );
        }

        layoutComponents.left.push( {
            type  : 'separator',
            params: {
                className: 'yn-responsive-hidden'
            }
        } );

        if ( showSchemaDropdown ) {
            layoutComponents.left.push( {
                type  : 'schemaDropdown',
                params: {
                    key: `${uid}_schemaDropdown`,

                    uid,

                    genericPermission: 'read',
                    value            : schema,
                    onChange         : this.onSchemaDropdownChange,

                    label     : __( 'YN.base.finder.schema.label' ),
                    inputStyle: { width: mobile ? '6rem' : '7rem' },
                    tabIndex  : tabIndexOffset + 60
                }
            } );
        }

        if ( showItemTypeDropdown ) {
            layoutComponents.left.push( {
                type  : 'itemTypeDropdown',
                params: {
                    key: `${uid}_itemTypeDropdown`,

                    uid,

                    schema,
                    rawMode,
                    showAdditionalRawNames,
                    genericPermission: 'read',
                    value            : itemTypeName,
                    markDirty        : true,
                    onChange         : this.onItemTypeDropdownChange,

                    label     : __( 'YN.base.finder.item.typeLabel' ),
                    inputStyle: { width: mobile ? '10rem' : '18rem' },
                    tabIndex  : tabIndexOffset + 70
                }
            } );
        }

        if ( showSearchField ) {
            layoutComponents.left.push( {
                type  : 'searchField',
                params: {
                    key: `${uid}_searchField`,

                    uid,

                    value    : searchString,
                    markDirty: true,
                    onChange : this.onSearchFieldChange,
                    onClear  : this.onSearchFieldClear,
                    onSearch : this.onSearchFieldSearch,

                    label     : __( 'YN.base.finder.searchString.label' ),
                    inputStyle: { width: mobile ? '10rem' : '18rem' },
                    tabIndex  : tabIndexOffset + 80
                }
            } );
        }

        if ( showAdvancedSearchOptions ) {
            layoutComponents.left.push( {
                type  : 'advancedSearchOptionsMenu',
                params: {
                    key: `${uid}_advancedSearchOptionsMenu`,
                    id : `${uid}_advancedSearchOptionsMenu`,

                    uid,

                    status,
                    timestamp,
                    timezone,
                    rawMode,
                    showAdditionalRawNames,
                    markDirty: true,
                    className: 'yn-responsive-hidden',
                    onChange : this.onAdvancedSearchOptionsMenuChange,
                    tabIndex : tabIndexOffset + 90
                }
            } );
        }

        layoutComponents.left.push( {
            type  : 'separator',
            params: {
                className: 'yn-responsive-hidden'
            }
        } );

        if ( showClearSearchButton ) {
            layoutComponents.left.push( {
                type  : 'clearSearchButton',
                params: {
                    key: `${uid}_clearSearchButton`,

                    className: 'yn-responsive-hidden',
                    onClick  : this.onClearSearchButtonClick,
                    tabIndex : tabIndexOffset + 100
                }
            } );
        }

        // right
        if ( showViewSelector ) {
            const viewOptions : ViewOption[] = [
                {
                    key  : 'search',
                    type : 'BOOLEAN',
                    label: __( 'YN.base.frontend.dataTable.viewSelector.option.search.label' )
                },
                {
                    key  : 'forceFit',
                    type : 'BOOLEAN',
                    label: __( 'YN.base.frontend.dataTable.viewSelector.option.forceFit.label' )
                }
            ];

            if ( editable ) {
                viewOptions.unshift( {
                    key  : 'editMode',
                    type : 'BOOLEAN',
                    label: __( 'YN.base.frontend.dataTable.viewSelector.option.editMode.label' )
                } );
            }

            layoutComponents.right.push( {
                type  : 'viewSelectorDropdown',
                params: {
                    key: `${uid}_viewSelectorDropdown`,

                    panelKey: uid,

                    module      : getModuleForViewSelector( schema, itemTypeName ),
                    views       : this.getViewsForViewSelectorDropdown( schema, itemTypeName, user! ),
                    selectedView: this.getSelectedViewForViewSelectorDropdown( schema, itemTypeName, user!, viewSelectorPanelProps ),
                    panelProps  : viewSelectorPanelProps,
                    viewOptions,
                    onSetView   : this.onViewSelectorSetView,
                    labeled     : true,

                    className: 'yn-responsive-hidden',
                    style    : { width: '18rem' },
                    tabIndex : tabIndexOffset + 110
                }
            } );
        }

        if ( showViewOptionsButton ) {
            layoutComponents.right.push( {
                type  : 'viewOptionsMenu',
                params: {
                    key: `${uid}_viewOptions`,

                    uid: uid,

                    options           : [
                        {
                            name    : 'editMode',
                            label   : __( 'YN.base.viewOptions.editMode.label' ),
                            value   : editMode,
                            disabled: !editable
                        },
                        {
                            name : 'showPropertyDefaults',
                            label: __( 'YN.base.viewOptions.propertyDefaults.label' ),
                            value: showPropertyDefaults
                        },
                        {
                            name : 'rawMode',
                            label: __( 'YN.base.viewOptions.rawMode.label' ),
                            value: rawMode
                        },
                        {
                            name    : 'showAdditionalRawNames',
                            label   : __( 'YN.base.viewOptions.additionalRawNames.label' ),
                            value   : showAdditionalRawNames,
                            disabled: rawMode
                        },
                        {
                            name : 'legend',
                            label: __( 'YN.base.viewOptions.legend.label' ),
                            value: legend
                        }
                    ],
                    className         : 'yn-responsive-hidden',
                    onViewOptionChange: this.onViewOptionChange,
                    tabIndex          : tabIndexOffset + 120
                }
            } );
        }

        if ( showColumnOptionsButton && user ) {
            layoutComponents.right.push( {
                type  : 'columnConfigMenu',
                params: {
                    key: `${uid}_columnConfig`,

                    uid,

                    columns                     : this.getAllColumns( columnDefinitions, columns, dataStructure, schema, status, timestamp, itemTypeName, rawMode, showAdditionalRawNames, user ) as ColumnConfigMenuColumn[],
                    onChange                    : this.onColumnVisibilityChange,
                    onExpandColumnsToHeaderWidth: this.onExpandColumnsToHeaderWidth,
                    onExpandColumnsToScreenWidth: this.onExpandColumnsToScreenWidth,
                    className                   : 'yn-responsive-hidden',
                    tabIndex                    : tabIndexOffset + 130
                }
            } );
        }

        if ( showColumnSortButton && user ) {
            layoutComponents.right.push( {
                type  : 'columnSortMenu',
                params: {
                    key: `${uid}_columnsort`,

                    uid,

                    columns  : this.getAllColumns( columnDefinitions, columns, dataStructure, schema, status, timestamp, itemTypeName, rawMode, showAdditionalRawNames, user ),
                    sorters  : this.getSortersWithField( sorters ),
                    onChange : this.onColumnSortChange,
                    className: 'yn-responsive-hidden',
                    tabIndex : tabIndexOffset + 140
                }
            } );
        }

        layoutComponents.right.push( {
            type  : 'separator',
            params: {
                className: 'yn-responsive-hidden'
            }
        } );

        if ( showReloadButton ) {
            layoutComponents.right.push( {
                type  : 'reloadButton',
                params: {
                    key: `${uid}_reloadButton`,

                    confirm : () => {
                        const modifiedData = getModifiedData( dataContext, schema );

                        return Boolean( modifiedData.items.length || modifiedData.references.length );
                    },
                    onClick : this.onReloadButtonClick,
                    tabIndex: tabIndexOffset + 150
                }
            } );
        }

        const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

        return <PrimeToolbar
            start={toolbarComponents.left}
            end={toolbarComponents.right}
            className="yn-panel-toolbar yn-toolbar-horizontal-separator-bottom"
        />;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        elusiveData: undefined,
        user       : undefined
    };

    if ( ownProps.uid ) {
        // get elusiveData
        if ( state.elusiveData?.[ ownProps.uid ] ) {
            retVal.elusiveData = state.elusiveData[ ownProps.uid ];
        }
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( Toolbar );
